"use client";

import React, { createContext, ReactNode, useMemo } from "react";

/**
 * Entity context data structure for Meridian
 */
export interface MeridianEntityContextData {
  entityType: string;
  entityId: string;
  entityData: Record<string, any>;
  entityDisplayName: string;
}

/**
 * Entity context type for Meridian
 */
interface MeridianEntityContextType {
  entity: MeridianEntityContextData | null;
}

/**
 * Create the Meridian entity context
 */
const MeridianEntityContext = createContext<MeridianEntityContextType | undefined>(undefined);

/**
 * Props for EntityContextProvider
 */
interface EntityContextProviderProps {
  children: ReactNode;
  entityType: string;
  entityId: string;
  entityData: Record<string, any>;
  entityDisplayName: string;
}

/**
 * EntityContextProvider component for Meridian
 * Provides entity context to Meridian chat components
 */
export function EntityContextProvider({
  children,
  entityType,
  entityId,
  entityData,
  entityDisplayName,
}: EntityContextProviderProps) {
  const value = useMemo<MeridianEntityContextType>(
    () => ({
      entity: {
        entityType,
        entityId,
        entityData,
        entityDisplayName,
      },
    }),
    [entityType, entityId, entityData, entityDisplayName]
  );

  return (
    <MeridianEntityContext.Provider value={value}>
      {children}
    </MeridianEntityContext.Provider>
  );
}

/**
 * Hook to access Meridian entity context
 * Throws error if used outside of EntityContextProvider
 */
export function useMeridianEntityContext(): MeridianEntityContextData {
  const context = React.useContext(MeridianEntityContext);
  
  if (!context || !context.entity) {
    throw new Error(
      "useMeridianEntityContext must be used within an EntityContextProvider"
    );
  }
  
  return context.entity;
}

/**
 * Hook to safely access Meridian entity context (returns null if not available)
 */
export function useMeridianEntityContextOptional(): MeridianEntityContextData | null {
  const context = React.useContext(MeridianEntityContext);
  return context?.entity || null;
}
